// src/pages/SurveyPage.js

import React, { useState, useEffect } from 'react';
import { db } from '../firebase';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import './SurveyPage.css';

function SurveyPage() {
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [guessedLocation, setGuessedLocation] = useState({ city: '', state: '' });
  const [showContactWarning, setShowContactWarning] = useState(false); // New state for custom modal

  // Form State - Defaulting scores to '10' and contact to false ('No')
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    contactRequested: false, 
    q1_goal: '',
    q2_urgency: '10',
    q3_relevance: '10',
    q4_progress: '10',
    q5_support: '10',
    q6_mostHelpful: '',
    q7_fallShort: ''
  });

  // Fetch general location on load based on IP
  useEffect(() => {
    fetch('https://ipapi.co/json/')
      .then((res) => res.json())
      .then((data) => {
        if (data.city && data.region_code) {
          setGuessedLocation({ city: data.city, state: data.region_code });
        }
      })
      .catch((err) => console.log('Could not fetch location', err));
  }, []);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleToggleChange = (value) => {
    setFormData((prev) => ({ ...prev, contactRequested: value }));
  };

  // Separated the actual database save logic so it can be called from multiple places
  const executeSubmission = async (finalContactRequested) => {
    setIsSubmitting(true);

    // Check if text answers Q1, Q6, Q7 are answered (Scores default to 10 so they are always "complete")
    const isComplete = 
      formData.q1_goal.trim() !== '' &&
      formData.q6_mostHelpful.trim() !== '' &&
      formData.q7_fallShort.trim() !== '';

    const status = isComplete ? 'active' : 'incomplete';
    const declinedText = 'user declined to answer';

    // Format data for Firestore
    const surveySubmission = {
      status: status,
      createdAt: serverTimestamp(),
      name: formData.name.trim() || declinedText,
      email: formData.email.trim() || declinedText,
      city: guessedLocation.city || declinedText,
      state: guessedLocation.state || declinedText,
      contactRequested: finalContactRequested, 
      responded: false,
      isFavorited: false,
      q1_goal: formData.q1_goal.trim() || declinedText,
      q2_urgency: Number(formData.q2_urgency),
      q3_relevance: Number(formData.q3_relevance),
      q4_progress: Number(formData.q4_progress),
      q5_support: Number(formData.q5_support),
      q6_mostHelpful: formData.q6_mostHelpful.trim() || declinedText,
      q7_fallShort: formData.q7_fallShort.trim() || declinedText,
    };

    try {
      await addDoc(collection(db, 'surveys'), surveySubmission);
      setIsSubmitted(true);
    } catch (error) {
      console.error("Error submitting survey: ", error);
      alert("There was an error submitting your survey. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handles the main submit button click
  const handleSubmit = (e) => {
    e.preventDefault();

    // --- Validation: Ensure contact info is provided if contact is requested ---
    if (formData.contactRequested && formData.name.trim() === '' && formData.email.trim() === '') {
      setShowContactWarning(true); // Show our custom modal instead of browser alert
      return; 
    }

    // If validation passes, submit normally
    executeSubmission(formData.contactRequested);
  };

  // Handler for the "Submit Anyway" button in the modal
  const handleSubmitAnyway = () => {
    setShowContactWarning(false);
    setFormData((prev) => ({ ...prev, contactRequested: false })); // Update the UI toggle to "No"
    executeSubmission(false); // Force contact requested to false in the database
  };

  const logoUrl = 'https://firebasestorage.googleapis.com/v0/b/steps4inclusiveai.firebasestorage.app/o/site-assets%2Frefugeai%2Frefugeai_logo01.jpg?alt=media&token=52a9fde1-198e-4cbb-a4e4-e116e2829bd2';

  // Helper for generating range slider
  const renderSlider = (name, minLabel, maxLabel) => (
    <div className="slider-container">
      <div className="slider-labels">
        <span className="slider-label-min">{minLabel}</span>
        <span className="slider-value-display">{formData[name]}</span>
        <span className="slider-label-max">{maxLabel}</span>
      </div>
      <div className="slider-wrapper">
        <input
          type="range"
          name={name}
          min="1"
          max="10"
          step="1"
          value={formData[name]}
          onChange={handleChange}
          className="styled-slider"
        />
        <div className="slider-track-labels">
          {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(num => (
            <span key={num} className={`tick-mark tick-${num}`}>{num}</span>
          ))}
        </div>
      </div>
    </div>
  );

  if (isSubmitted) {
    return (
      <div className="survey-page">
        <div className="survey-container success-container">
          <img src={logoUrl} alt="Refuge Ai Logo" className="survey-logo" />
          <h2>Thank you for your time and honesty.</h2>
          <p>Your feedback has been successfully submitted.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="survey-page">
      
      {/* Custom Contact Warning Modal */}
      {showContactWarning && (
        <div className="custom-modal-overlay">
          <div className="custom-modal-content">
            <h3>Missing Contact Info</h3>
            <p>If you do not add your contact info, then we cannot contact you. Would you like to go back and add your info?</p>
            <div className="custom-modal-actions">
              <button 
                type="button" 
                className="warning-btn-add" 
                onClick={() => setShowContactWarning(false)}
              >
                Add My Info
              </button>
              <button 
                type="button" 
                className="warning-btn-submit" 
                onClick={handleSubmitAnyway}
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Submitting...' : 'Submit Anyway'}
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="survey-container">
        <div className="survey-header">
          <img src={logoUrl} alt="Refuge Ai Logo" className="survey-logo" />
          <h1>Impact Assessment</h1>
          <p className="intro-text">Thank you for taking a few minutes to provide your feedback.</p>
          <p className="confidential-text">Please know that your answers are confidential and will not affect your access to support.</p>
          <p className="okay-text">It is okay to report that the support you received was not helpful.</p>
        </div>

        <form onSubmit={handleSubmit} className="survey-form">
          
          <div className="personal-info-row">
            <div className="form-group-inline">
              <label>Name</label>
              <input type="text" name="name" placeholder="(Optional)" value={formData.name} onChange={handleChange} />
            </div>
            
            <div className="form-group-inline">
              <label>Email Address</label>
              <input type="email" name="email" placeholder="(Optional)" value={formData.email} onChange={handleChange} />
            </div>
          </div>

          <div className="form-group-inline toggle-container">
            <label>Would you like someone to contact you?</label>
            <div className="toggle-buttons">
              <button 
                type="button" 
                className={`toggle-btn ${formData.contactRequested ? 'active' : ''}`} 
                onClick={() => handleToggleChange(true)}
              >
                Yes
              </button>
              <button 
                type="button" 
                className={`toggle-btn ${!formData.contactRequested ? 'active' : ''}`} 
                onClick={() => handleToggleChange(false)}
              >
                No
              </button>
            </div>
          </div>

          <div className="form-group">
            <label>1. What goal were you trying to accomplish when you used Refuge Ai?</label>
            <textarea name="q1_goal" value={formData.q1_goal} onChange={handleChange} rows="3"></textarea>
          </div>

          <div className="form-group">
            <label>2. How urgent was it for you to accomplish this goal at that time?</label>
            {renderSlider('q2_urgency', 'No Urgency', 'Extremely Urgent')}
          </div>

          <div className="form-group">
            <label>3. How useful was the support you received from Refuge Ai in helping you make progress toward that goal?</label>
            {renderSlider('q3_relevance', 'Not Useful', 'Extremely Useful')}
          </div>

          <div className="form-group">
            <label>4. How much progress have you made toward accomplishing that goal?</label>
            {renderSlider('q4_progress', 'No Progress', 'Significant Progress')}
          </div>

          <div className="form-group">
            <label>5. How much of that progress can you attribute to the support you received from Refuge Ai?</label>
            {renderSlider('q5_support', 'None', 'All of the Progress')}
          </div>

          <div className="form-group">
            <label>6. What part of Refuge Ai was most helpful?</label>
            <textarea name="q6_mostHelpful" value={formData.q6_mostHelpful} onChange={handleChange} rows="3"></textarea>
          </div>

          <div className="form-group">
            <label>7. In what ways did the support you received from Refuge Ai fall short in helping you accomplish your goal?</label>
            <textarea name="q7_fallShort" value={formData.q7_fallShort} onChange={handleChange} rows="3"></textarea>
          </div>

          <button type="submit" className="submit-btn" disabled={isSubmitting}>
            {isSubmitting ? 'Submitting...' : 'Submit'}
          </button>
        </form>
      </div>
    </div>
  );
}

export default SurveyPage;